import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Pill, Check, X as XIcon, Plus, Clock, AlertTriangle, Repeat, MinusCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

const ACTION_META = {
  new:               { icon: Plus,         label: "Add new medication",   bg: "bg-emerald-50",  fg: "text-emerald-700" },
  discontinue:       { icon: MinusCircle,  label: "Discontinue",          bg: "bg-rose-50",     fg: "text-rose-700" },
  dose_change:       { icon: ArrowRight,   label: "Change dose",          bg: "bg-amber-50",    fg: "text-amber-700" },
  frequency_change:  { icon: Repeat,       label: "Change frequency",     bg: "bg-sky-50",      fg: "text-sky-700" },
};

function DiffRow({ before, after }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="line-through text-muted-foreground">{before || "—"}</span>
      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
      <span className="font-semibold text-foreground">{after || "—"}</span>
    </div>
  );
}

function RequestCard({ req, clientName, onAction }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = ACTION_META[req.action] || ACTION_META.new;
  const Icon = meta.icon;
  const decided = req.status !== "pending";

  const fire = async (action) => {
    setBusy(true);
    try { await onAction(req.id, action, notes); }
    finally { setBusy(false); }
  };

  return (
    <Card className={`p-5 ${decided ? "opacity-70" : ""}`} data-testid={`med-request-card-${req.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`h-10 w-10 rounded-md flex items-center justify-center ${meta.bg} ${meta.fg}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-bold">{meta.label} — {req.name}</p>
            <Badge
              variant={req.status === "pending" ? "secondary" : req.status === "approved" ? "default" : "destructive"}
              data-testid={`med-request-status-${req.id}`}
            >
              {req.status}
            </Badge>
            {req.is_prn && <Badge variant="outline" className="border-amber-400 text-amber-700">PRN</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">
            For <strong>{clientName || "client"}</strong> · From <strong>{req.provider_name}</strong>
          </p>
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" /> Submitted {fmt(req.created_at)}
          </p>
        </div>
      </div>

      <div className="bg-secondary/50 rounded-md p-3 text-sm space-y-1.5 mb-3">
        {req.action === "new" && (
          <>
            <p><strong>Medication:</strong> {req.name}</p>
            <p><strong>Dose:</strong> {req.dose}</p>
            <p><strong>Schedule:</strong> {req.schedule || "AM/PM"}</p>
            <p><strong>Type:</strong> {req.is_prn ? "PRN (As-Needed)" : "Routine"}</p>
          </>
        )}
        {req.action === "discontinue" && (
          <>
            <p><strong>Current:</strong> {req.name} · {req.dose} · {req.schedule}</p>
            <p className="text-rose-700"><strong>Action:</strong> Mark inactive (live eMAR will hide this med)</p>
          </>
        )}
        {req.action === "dose_change" && (
          <>
            <p><strong>{req.name}</strong> · Schedule: {req.schedule}</p>
            <div><strong>Dose:</strong> <DiffRow before={req.dose} after={req.proposed_dose} /></div>
          </>
        )}
        {req.action === "frequency_change" && (
          <>
            <p><strong>{req.name}</strong> · Dose: {req.dose}</p>
            <div><strong>Schedule:</strong> <DiffRow before={req.schedule} after={req.proposed_schedule} /></div>
          </>
        )}
        {req.reason && (
          <p className="pt-1 border-t border-border mt-1.5"><strong>Reason:</strong> {req.reason}</p>
        )}
      </div>

      {decided ? (
        <div className="text-xs text-muted-foreground">
          {req.status === "approved" ? "Approved" : "Rejected"} by <strong>{req.decided_by || "—"}</strong> · {fmt(req.resolved_at)}
          {req.decision_notes && <p className="mt-1 italic">Notes: {req.decision_notes}</p>}
        </div>
      ) : (
        <>
          <Textarea
            rows={2}
            placeholder="Optional decision notes…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid={`med-decision-notes-${req.id}`}
            className="mb-2"
          />
          <div className="flex gap-2">
            <Button onClick={() => fire("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`med-approve-${req.id}`}>
              <Check className="w-4 h-4 mr-1" /> {busy ? "Working…" : "Approve & apply"}
            </Button>
            <Button onClick={() => fire("reject")} disabled={busy} variant="destructive" data-testid={`med-reject-${req.id}`}>
              <XIcon className="w-4 h-4 mr-1" /> {busy ? "Working…" : "Reject"}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

export default function AdminMedRequests() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);

  const load = async () => {
    const [{ data: reqs }, { data: cs }] = await Promise.all([
      api.get(`/med-requests${tab !== "all" ? `?status=${tab}` : ""}`),
      api.get("/clients"),
    ]);
    setRows(reqs);
    setClients(cs);
  };
  useEffect(() => { load(); }, [tab]);

  const clientNameById = useMemo(() => {
    const m = {};
    for (const c of clients) m[c.id] = c.name;
    return m;
  }, [clients]);

  const handle = async (id, action, decision_notes) => {
    try {
      await api.post(`/med-requests/${id}/${action}`, { decision_notes });
      toast.success(action === "approve" ? "Approved · live eMAR updated" : "Request rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-med-requests">
      <div>
        <p className="text-label">Approval Queue</p>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <Pill className="w-6 h-6 text-primary" /> Medication Change Requests
        </h1>
        <p className="text-sm text-muted-foreground flex items-start gap-1.5 mt-1">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          Contractors propose every medication change here. The live eMAR regimen
          only mutates after you approve — denials are stamped for audit retention.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="med-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="med-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="med-tab-rejected">Rejected</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="space-y-3 mt-4">
          {rows.length === 0 && (
            <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="med-empty-state">
              {tab === "pending" ? "No pending medication requests." : `No ${tab} requests yet.`}
            </Card>
          )}
          {rows.map((r) => (
            <RequestCard key={r.id} req={r} clientName={clientNameById[r.client_id]} onAction={handle} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
